import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Collapse, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import Loading from '../../../../common/Loading';
import Swal from '../../../../helpers/swal';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const isActiveStatus = (value) => value === true || value === 'true';

function AddBrandForm({ clinicId, onSuccess, onCancel }) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({ defaultValues: { isActive: 'true' } });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = (data) => {
        setIsSubmitting(true);
        clinicService
            .addUpdateLens(clinicId, data)
            .then(() => {
                toast.success('Lens brand added', { position: toast.POSITION.BOTTOM_RIGHT });
                reset({ isActive: 'true', brandName: '' });
                onSuccess?.();
            })
            .catch(() => {
                toast.error('Failed to add brand', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="clinic-lens-add-form">
            <div className="notifications-form-label mb-3">
                <i className="bi bi-plus-circle me-1"></i>
                Add New Lens Brand
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row g-3">
                    <div className="col-md-3">
                        <label htmlFor="lensStatus" className="notifications-form-label">
                            Status
                        </label>
                        <select id="lensStatus" className="form-select form-select-sm" disabled={isSubmitting} {...register('isActive', { required: true })}>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="col-md-9">
                        <label htmlFor="brandName" className="notifications-form-label">
                            Brand Name <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            id="brandName"
                            className={`form-control form-control-sm ${errors.brandName ? 'is-invalid' : ''}`}
                            placeholder="Enter brand name"
                            disabled={isSubmitting}
                            {...register('brandName', { required: 'Brand name is required' })}
                        />
                        {errors.brandName && <div className="invalid-feedback">{errors.brandName.message}</div>}
                    </div>
                </div>
                <div className="clinic-lens-form-actions">
                    {onCancel && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </button>
                    )}
                    <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Saving…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-floppy me-1"></i>
                                Save Brand
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

function LensTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lensList, setLensList] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const { clinicId } = useParams();

    useEffect(() => {
        fetchLensList();
    }, [clinicId]);

    const fetchLensList = () => {
        setLoading(true);
        setError(null);
        clinicService
            .getLens(clinicId)
            .then((res) => setLensList(res.data || []))
            .catch(() => {
                setError('Failed to load lens brands.');
                setLensList([]);
            })
            .finally(() => setLoading(false));
    };

    const handleStatusChange = (lensId, newStatus, brandName) => {
        clinicService
            .addUpdateLens(clinicId, {
                id: lensId,
                isActive: newStatus,
                clinicId,
                brandName,
            })
            .then(() => fetchLensList())
            .catch(() => toast.error('Failed to update status', { position: toast.POSITION.BOTTOM_RIGHT }));
    };

    const handleDelete = (lensId, brandName) => {
        Swal.fire({
            title: 'Delete Brand?',
            html: `Are you sure you want to delete <strong>${brandName}</strong>?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm',
                title: 'card-title',
                html: 'small',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                clinicService
                    .deleteLens(clinicId, lensId)
                    .then(() => {
                        fetchLensList();
                        Swal.fire('Deleted!', 'The brand has been deleted.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Error!', 'Failed to delete the brand.', 'error');
                        fetchLensList();
                    });
            }
        });
    };

    const activeCount = lensList.filter((l) => isActiveStatus(l.isActive)).length;
    const inactiveCount = lensList.length - activeCount;

    return (
        <div className="clinic-lens-panel">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div>
                    <h5 className="clinic-lens__title mb-1">
                        <i className="bi bi-eye-fill text-primary me-1"></i>
                        Contact Lens Brands
                    </h5>
                    <p className="text-muted small mb-0">Manage contact lens brands available for this clinic.</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={`btn btn-sm ${showAddForm ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
                        {showAddForm ? 'Cancel' : 'Add Brand'}
                    </button>
                    {lensList.length > 0 && (
                        <button
                            type="button"
                            className={`btn btn-sm ${editMode ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                            onClick={() => setEditMode(!editMode)}
                        >
                            <i className={`bi ${editMode ? 'bi-x-lg' : 'bi-pencil'} me-1`}></i>
                            {editMode ? 'Done Editing' : 'Edit Brands'}
                        </button>
                    )}
                </div>
            </div>

            {error && !loading && (
                <div className="alert alert-warning border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </div>
            )}

            <div className="notifications-panel">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-building"></i>
                        Lens Brands
                        {!loading && lensList.length > 0 && (
                            <span className="text-muted small fw-normal">
                                ({lensList.length} total)
                            </span>
                        )}
                    </span>
                </div>

                <div className="notifications-panel__body">
                    <Collapse in={showAddForm}>
                        <div className="mb-3">
                            <AddBrandForm
                                clinicId={clinicId}
                                onSuccess={() => {
                                    setShowAddForm(false);
                                    fetchLensList();
                                }}
                                onCancel={() => setShowAddForm(false)}
                            />
                        </div>
                    </Collapse>

                    {!loading && lensList.length > 0 && (
                        <div className="clinic-lens-toolbar">
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <span className="clinic-lens-stat clinic-lens-stat--total">{lensList.length} total</span>
                                <span className="clinic-lens-stat clinic-lens-stat--active">{activeCount} active</span>
                                <span className="clinic-lens-stat clinic-lens-stat--inactive">{inactiveCount} inactive</span>
                            </div>
                            {editMode && (
                                <span className="clinic-lens-edit-hint">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Toggle status or delete brands below.
                                </span>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <Loading />
                    ) : lensList.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-lens-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: editMode ? '30%' : '25%' }}>
                                            <i className="bi bi-toggle-on"></i> Status
                                        </th>
                                        <th>
                                            <i className="bi bi-building"></i> Brand Name
                                        </th>
                                        {editMode && (
                                            <th className="text-end" style={{ width: '6rem' }}>
                                                <i className="bi bi-gear"></i> Actions
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lensList.map((lens) => {
                                        const active = isActiveStatus(lens.isActive);
                                        return (
                                            <tr key={lens.id} className="clinic-lens-row">
                                                <td>
                                                    {editMode ? (
                                                        <div className="form-check form-switch mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`status-${lens.id}`}
                                                                checked={active}
                                                                onChange={(e) => handleStatusChange(lens.id, e.target.checked, lens.brandName)}
                                                            />
                                                            <label className="form-check-label small" htmlFor={`status-${lens.id}`}>
                                                                {active ? 'Active' : 'Inactive'}
                                                            </label>
                                                        </div>
                                                    ) : (
                                                        <span className={`clinic-lens-status-badge ${active ? 'clinic-lens-status-badge--active' : 'clinic-lens-status-badge--inactive'}`}>
                                                            {active ? (
                                                                <>
                                                                    <i className="bi bi-check-circle"></i> Active
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-x-circle"></i> Inactive
                                                                </>
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="clinic-lens-brand-name">{lens.brandName}</span>
                                                </td>
                                                {editMode && (
                                                    <td className="text-end">
                                                        <ActionTooltipButton
                                                            tooltip="Delete brand"
                                                            tooltipId={`delete-lens-${lens.id}`}
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(lens.id, lens.brandName)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </ActionTooltipButton>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="clinic-lens-empty">
                            <i className="bi bi-eye-slash"></i>
                            No lens brands configured.
                            {!showAddForm && (
                                <div className="mt-3">
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowAddForm(true)}>
                                        <i className="bi bi-plus-lg me-1"></i>
                                        Add First Brand
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LensTab;
